import React, { PureComponent } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import styled from 'styled-components';
import isEqual from 'lodash/fp/isEqual';
import moment from 'moment';
import { FocusedInputShape, Colors, AnalyticsPage } from '@inplayer-org/inplayer-ui';

// Actions
import {
  loadReportInformations as loadReportInformationsAction,
  loadRevenuePerCurrency as loadRevenuePerCurrencyAction,
  fetchReport as fetchReportAction,
  FetchReportParams,
  LoadReportsCommonParams,
} from 'ducks/merchantDucks/reports/actions';
import {
  SubscriptionParams,
  fetchSubscriptions as fetchSubscriptionsAction,
  SubscriptionFilterTypes,
  fetchSubscriptionsStatus,
} from 'ducks/merchantDucks/subscriptions/actions';
import { saveDatePickerPeriod as saveDatePickerPeriodAction } from 'ducks/merchantDucks/datepicker/actions';

// types
import ReportsState from 'ducks/merchantDucks/reports/types';
import RootState from 'ducks/RootState';

// utils
import { withTopNav } from 'components/Navigation';
import media from 'utils/mediaQueries';

// selectors
import { getUserDetails } from 'ducks/merchantDucks/user/selectors';
import { getReportsState } from 'ducks/merchantDucks/reports/selectors';

// components
import MainAnalytics from 'pages/MerchantPages/Analytics/components/MainAnalytics';
import Subheader, { ONE_MONTH, TODAY } from 'pages/MerchantPages/Analytics/components/Subheader';
import RevenueInfoBox, { REVENUE } from 'pages/MerchantPages/Analytics/Revenue/RevenueInfoBox';
import PaymentsInfoBox, { PAYMENTS } from 'pages/MerchantPages/Analytics/Payments/PaymentsInfoBox';
import CustomersInfoBox, { REGISTERED_CUSTOMERS } from 'pages/MerchantPages/Analytics/Customers/CustomersInfoBox';
import SubscriptionsInfoBox, { SUBSCRIPTIONS } from 'pages/MerchantPages/Analytics/Subscriptions/SubscriptionsInfoBox';
import TourGuideWrapper from 'components/TourGuide';

export const format = 'YYYY-MM-DD HH:mm:ss';

interface DateInterval {
  startDate: moment.Moment;
  endDate: moment.Moment;
  interval: string;
  period: string;
}

interface State {
  report: number;
  activeIcon: number;
  startDate: moment.Moment;
  endDate: moment.Moment;
  interval: string;
  lineChart: boolean;
  focusedInput: FocusedInputShape | null;
  period: string;
}

interface StateProps {
  reports: ReportsState;
  user: any;
}

interface DispatchProps {
  loadReportInformations: (params: LoadReportsCommonParams) => any;
  loadRevenuePerCurrency: (params: LoadReportsCommonParams) => any;
  fetchReport: (params: FetchReportParams) => any;
  fetchSubscriptions: (type: SubscriptionFilterTypes, params?: SubscriptionParams) => any;
  saveDatePickerPeriod: any;
}

type Props = StateProps & DispatchProps;

export const Section = styled.div`
  width: 86%;
  margin: 1rem auto;
`;

const InfoBoxContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-column-gap: 5rem;
  margin-top: 1.8rem;

  ${media.customMinWidth(2100)} {
    grid-column-gap: 7rem;
  }
`;

const activeBoxMapper = {
  1: 'audience',
  2: 'revenue',
  3: 'payments',
  4: 'subscription',
};

class Analytics extends PureComponent<Props, State> {
  state = {
    report: REVENUE,
    activeIcon: REVENUE,
    startDate: moment().startOf('day').subtract(1, 'month'),
    endDate: moment().endOf('day'),
    interval: 'day',
    focusedInput: null,
    lineChart: true,
    period: ONE_MONTH,
  };

  componentDidMount() {
    this.loadReports();
  }

  componentDidUpdate(prevProps: Props) {
    const { user } = this.props;
    const { user: prevUser } = prevProps;

    if (!isEqual(user)(prevUser)) {
      this.loadReports();
    }
  }

  handleFocusChange = (focusedInput: FocusedInputShape | null) => {
    this.setState({ focusedInput });
  };

  render() {
    const { reports, fetchReport, loadReportInformations, loadRevenuePerCurrency, fetchSubscriptions } = this.props;
    const { startDate, endDate, activeIcon, interval, lineChart, report, focusedInput, period } = this.state;
    const start = period === TODAY ? startDate.format(format) : startDate.clone().startOf('day').format(format);
    const end = period === TODAY ? endDate.format(format) : endDate.clone().endOf('day').format(format);

    return (
      <AnalyticsPage tag={`main_tab_${activeBoxMapper[activeIcon]}`} type="tab">
        <>
          <TourGuideWrapper />
          <Subheader
            startDate={startDate}
            endDate={endDate}
            focusedInput={focusedInput}
            onDateChange={this.onDateChange}
            handleFocusChange={this.handleFocusChange}
          />
          <Section>
            <InfoBoxContainer data-tut="reactour__analytics-sections">
              <RevenueInfoBox
                active={activeIcon === REVENUE}
                handleIconClick={this.handleIconClick}
                startDate={start}
                endDate={end}
                interval={interval}
                fetchReport={fetchReport}
                loadReportInformations={loadReportInformations}
                loadRevenuePerCurrency={loadRevenuePerCurrency}
                lineChart={lineChart}
                revenuePerCurrency={reports.revenuePerCurrency}
                color={Colors.skyBlue}
              />
              <CustomersInfoBox
                active={activeIcon === REGISTERED_CUSTOMERS}
                handleIconClick={this.handleIconClick}
                startDate={start}
                endDate={end}
                interval={interval}
                fetchReport={fetchReport}
                loadReportInformations={loadReportInformations}
                loadRevenuePerCurrency={loadRevenuePerCurrency}
                lineChart={lineChart}
                reportInformation={reports.reportInformation}
                color={Colors.red}
              />
              <PaymentsInfoBox
                active={activeIcon === PAYMENTS}
                handleIconClick={this.handleIconClick}
                startDate={start}
                endDate={end}
                interval={interval}
                fetchReport={fetchReport}
                loadReportInformations={loadReportInformations}
                loadRevenuePerCurrency={loadRevenuePerCurrency}
                lineChart={lineChart}
                reportInformation={reports.reportInformation}
                color={Colors.blue}
              />
              <SubscriptionsInfoBox
                active={activeIcon === SUBSCRIPTIONS}
                handleIconClick={this.handleIconClick}
                startDate={start}
                endDate={end}
                interval={interval}
                fetchReport={fetchReport}
                fetchSubscriptions={fetchSubscriptions}
                loadReportInformations={loadReportInformations}
                loadRevenuePerCurrency={loadRevenuePerCurrency}
                lineChart={lineChart}
                reportInformation={reports.reportInformation}
                color={Colors.green}
                subscriptionsStatus={fetchSubscriptionsStatus}
              />
            </InfoBoxContainer>
            <MainAnalytics
              interval={interval}
              type={report}
              reports={reports.histogram}
              isFetching={reports.isFetching}
              reportInformation={reports.reportInformation}
            />
          </Section>
        </>
      </AnalyticsPage>
    );
  }

  loadReports = () => {
    const { fetchReport, loadReportInformations, loadRevenuePerCurrency } = this.props;
    const { report, startDate, endDate, interval, lineChart } = this.state;

    fetchReport({
      icon: report,
      startDate: startDate.format(format),
      endDate: endDate.format(format),
      interval,
      lineChart,
    });

    const params = {
      startDate: startDate.format(format),
      endDate: endDate.format(format),
    };

    loadReportInformations(params);
    loadRevenuePerCurrency(params);
  };

  handleIconClick = (icon: number) => {
    this.setState({
      activeIcon: icon,
      report: icon,
    });
  };

  onDateChange = ({ startDate, endDate, interval, period }: DateInterval) => {
    const { saveDatePickerPeriod } = this.props;
    saveDatePickerPeriod(startDate, endDate);
    this.setState({ startDate, endDate, interval, period });
  };
}

export const PlainAnalyticsComponent = Analytics;

export const mapStateToProps: MapStateToProps<StateProps, {}, RootState> = (state) => ({
  reports: getReportsState(state),
  user: getUserDetails(state),
});

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, {}> = {
  loadReportInformations: loadReportInformationsAction,
  loadRevenuePerCurrency: loadRevenuePerCurrencyAction,
  fetchReport: fetchReportAction,
  fetchSubscriptions: fetchSubscriptionsAction,
  saveDatePickerPeriod: saveDatePickerPeriodAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTopNav<Props>(Analytics));
